import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import * as Sentry from "@sentry/react";
import { SELF_SERVE_ONBOARDING_ENABLED } from "@/config/featureFlags";
import { isStaffProvisionedUser } from "@/lib/accountAccess";

type AppRole = "admin" | "user";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
  /**
   * True when the signed-in user OWNS a business (a `businesses` row keyed to
   * their user_id), independent of the platform-admin `accounts.role`. This is
   * the gate for accessing one's OWN dashboard. Kept separate from `isAdmin`
   * (platform super-admin) so that downgrading owners off the global admin role
   * (the P0 RLS fix) does not lock them out of their own workspace.
   */
  ownsBusiness: boolean;
  isStaff: boolean;
  staffOutletId: string | null;
  staffBusinessId: string | null;
  staffBusinesses: Array<{ business_id: string; business_name: string; slug: string | null }> | null;
  isSupportCollaborator: boolean;
  collaboratorBusinesses: Array<{ business_id: string; business_name: string; slug: string | null }> | null;
  collaboratorExpiresAt: string | null;
  businessId: string | null;
  onboardingComplete: boolean | null;
  subscriptionTier: string | null;
  hasBookingsAddon: boolean;
  isPasswordRecovery: boolean;
  signInWithGoogle: () => Promise<{ data?: any, error: Error | null }>;
  signInWithEmail: (email: string, password: string) => Promise<{ data?: any, error: Error | null }>;
  signUpWithEmail: (email: string, password: string) => Promise<{ data?: any, error: Error | null }>;
  resetPasswordForEmail: (email: string) => Promise<{ data?: any, error: Error | null }>;
  refreshBusinessContext: () => Promise<void>;
  signOut: () => Promise<void>;
  switchStaffBusiness: (businessId: string) => void;
  switchCollaboratorBusiness: (businessId: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const BUSINESS_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const initialCollaboratorBusinessId = () => {
  const requested = new URLSearchParams(window.location.search)
    .get("collaborator_business")
    ?.trim();
  if (requested && BUSINESS_ID_PATTERN.test(requested)) {
    // This only expresses navigation intent. The query below activates it only
    // when the signed-in user has a live collaborator grant for the business.
    localStorage.setItem("dibs_collab_active_business", requested);
    return requested;
  }
  return localStorage.getItem("dibs_collab_active_business");
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);
  const [selectedStaffBusinessId, setSelectedStaffBusinessId] = useState<string | null>(() => localStorage.getItem("dibs_staff_active_business"));
  const [selectedCollabBusinessId, setSelectedCollabBusinessId] = useState<string | null>(initialCollaboratorBusinessId);

  const {
    data: businessData,
    isLoading: businessLoading,
    error: businessError,
    refetch: refetchBusinessData,
  } = useQuery({
    queryKey: ["auth_business_data", user?.id, selectedStaffBusinessId, selectedCollabBusinessId],
    queryFn: async () => {
      if (!user) return null;
      console.log("[AuthContext] Fetching business and admin data for:", user.id);
      
      const [adminResult, businessResult] = await Promise.all([
        supabase
          .from("accounts")
          .select("role")
          .eq("user_id", user.id)
          .eq("role", "admin")
          .maybeSingle(),
        supabase
          .from("businesses")
          .select("business_id, onboarding_complete, subscription_tier, is_enterprise_setup, has_bookings_addon")
          .eq("user_id", user.id)
          .maybeSingle()
      ]);

      if (adminResult.error) console.error("[AuthContext] Admin check error:", adminResult.error);
      if (businessResult.error) console.error("[AuthContext] Business check error:", businessResult.error);

      let isAdmin = !!adminResult.data;
      // Owning a business grants access to one's OWN dashboard regardless of the
      // platform-admin role. `treatAsOwner` drives which business gets activated
      // below; the staff-priority edge case can flip it off so an accidental
      // owner with a staff invite still skips onboarding (see below).
      const ownsBusiness = !!businessResult.data?.business_id;
      let treatAsOwner = isAdmin || ownsBusiness;
      let isStaff = false;
      let staffOutletId: string | null = null;
      let staffBusinessId: string | null = null;

      // Staff invites are NOT claimed here. Activation is token-gated and runs
      // service-side in the accept-staff-invite edge function. Matching on the
      // signed-in email alone would let anyone who guessed an invited address
      // take the seat. By the time a staff member reaches this code their rows
      // are already active and linked to their user_id.

      // Check for active staff records (may have multiple across outlets/businesses)
      const { data: staffResults } = await supabase
        .from("staff_members")
        .select("business_id, outlet_id, status")
        .eq("user_id", user.id)
        .eq("status", "active")
        .order("created_at", { ascending: true });

      const uniqueBusinessIds = Array.from(new Set(staffResults?.map((r) => r.business_id) || []));
      let staffBusinesses: Array<{ business_id: string; business_name: string; slug: string | null }> = [];

      if (uniqueBusinessIds.length > 0) {
        const { data: businesses } = await supabase
          .from("businesses")
          .select("business_id, business_name, slug")
          .in("business_id", uniqueBusinessIds);
        staffBusinesses = businesses || [];
      }

      const staffResult = staffResults?.[0] || null;

      if (staffResult) {
        isStaff = true;
        staffOutletId = staffResult.outlet_id;
        staffBusinessId = staffResult.business_id;
      }

      // Support collaborator (Dibs team, full-access, time-boxed)
      // Link any invites for this email to this user (server-side, link-only,
      // a still-pending invite can't self-activate; only the owner's approval
      // flips status to 'active'). Then resolve active, unexpired grants.
      let isSupportCollaborator = false;
      let collaboratorBusinesses: Array<{ business_id: string; business_name: string; slug: string | null }> = [];
      let collaboratorExpiresAt: string | null = null;
      try {
        await supabase.functions.invoke("collaborator-link-self", { body: {} });
      } catch {
        /* best-effort; resolution below still works for already-linked rows */
      }
      const nowIso = new Date().toISOString();
      // Active grants are indefinite (expires_at NULL) until the owner revokes;
      // honour an optional expiry only if one was explicitly set.
      const { data: collabRows } = await supabase
        .from("support_collaborators" as any)
        .select("business_id, expires_at")
        .eq("collaborator_user_id", user.id)
        .eq("status", "active")
        .or(`expires_at.is.null,expires_at.gt.${nowIso}`);
      const collabBizIds = Array.from(
        new Set(((collabRows as any[]) || []).map((r) => r.business_id))
      );
      if (collabBizIds.length > 0) {
        isSupportCollaborator = true;
        collaboratorExpiresAt =
          ((collabRows as any[]) || [])
            .map((r) => r.expires_at as string)
            .sort()[0] || null;
        const { data: cbiz } = await supabase
          .from("businesses")
          .select("business_id, business_name, slug")
          .in("business_id", collabBizIds)
          .order("business_name", { ascending: true });
        collaboratorBusinesses = cbiz || [];
      }

      // If the user accidentally created an admin account (signed in before invite)
      // but hasn't completed onboarding, and they have a staff invite,
      // prioritize the staff role so they skip onboarding.
      const adminOnboardingComplete = !!businessResult.data?.onboarding_complete;
      const adminEnterpriseSetup = !!businessResult.data?.is_enterprise_setup;
      if (isStaff && treatAsOwner && !adminOnboardingComplete && !adminEnterpriseSetup) {
        isAdmin = false;
        treatAsOwner = false;
      }

      let activeBusinessId = null;
      let activeOnboardingComplete = false;
      let activeSubscriptionTier = null;
      let activeHasBookingsAddon = false;

      // An operator can ALSO own their own business (isAdmin). An explicit
      // collaborator-business selection must win over the admin default, or
      // owner-operators could never enter the client dashboard they were
      // granted access to.
      const wantsCollabSession =
        isSupportCollaborator &&
        !!selectedCollabBusinessId &&
        collaboratorBusinesses.some((b) => b.business_id === selectedCollabBusinessId);

      if (treatAsOwner && !wantsCollabSession) {
        activeBusinessId = businessResult.data?.business_id || null;
        // Enterprise users who have been approved (is_enterprise_setup=true)
        // should always be treated as onboarding-complete, even if the
        // onboarding_complete flag wasn't set for some reason.
        activeOnboardingComplete = adminOnboardingComplete || adminEnterpriseSetup;
        activeSubscriptionTier = businessResult.data?.subscription_tier || null;
        activeHasBookingsAddon = !!businessResult.data?.has_bookings_addon;
      } else if (isStaff && !wantsCollabSession) {
        // Evaluate active staff business based on selection or fallback
        if (selectedStaffBusinessId && staffBusinesses.some(b => b.business_id === selectedStaffBusinessId)) {
          staffBusinessId = selectedStaffBusinessId;
        } else if (staffBusinesses.length > 0) {
          staffBusinessId = staffBusinesses[0].business_id;
          // Update selected if not valid
          if (selectedStaffBusinessId !== staffBusinessId) {
            localStorage.setItem("dibs_staff_active_business", staffBusinessId);
          }
        }
        
        activeBusinessId = staffBusinessId;
        
        // Fetch the business details for the staff member's business
        const { data: staffBusiness } = await supabase
          .from("businesses")
          .select("onboarding_complete, subscription_tier, has_bookings_addon")
          .eq("business_id", staffBusinessId)
          .maybeSingle();

        if (staffBusiness) {
          activeOnboardingComplete = !!staffBusiness.onboarding_complete;
          activeSubscriptionTier = staffBusiness.subscription_tier;
          activeHasBookingsAddon = !!staffBusiness.has_bookings_addon;
        }
      } else if (isSupportCollaborator) {
        // Operator helping a client. Pick the selected collaborator business, or
        // fall back to the first. Collaborators always skip onboarding.
        let collabBusinessId = collaboratorBusinesses[0]?.business_id || null;
        if (
          selectedCollabBusinessId &&
          collaboratorBusinesses.some((b) => b.business_id === selectedCollabBusinessId)
        ) {
          collabBusinessId = selectedCollabBusinessId;
        } else if (collabBusinessId && selectedCollabBusinessId !== collabBusinessId) {
          localStorage.setItem("dibs_collab_active_business", collabBusinessId);
        }
        activeBusinessId = collabBusinessId;

        const { data: collabBusiness } = await supabase
          .from("businesses")
          .select("onboarding_complete, subscription_tier, has_bookings_addon")
          .eq("business_id", collabBusinessId)
          .maybeSingle();
        if (collabBusiness) {
          activeOnboardingComplete = true;
          activeSubscriptionTier = collabBusiness.subscription_tier;
          activeHasBookingsAddon = !!collabBusiness.has_bookings_addon;
        }
      }

      return {
        isAdmin,
        ownsBusiness,
        isStaff,
        staffOutletId,
        staffBusinessId,
        staffBusinesses,
        isSupportCollaborator,
        collaboratorBusinesses,
        collaboratorExpiresAt,
        businessId: activeBusinessId,
        onboardingComplete: activeOnboardingComplete,
        subscriptionTier: activeSubscriptionTier,
        hasBookingsAddon: activeHasBookingsAddon,
      };
    },
    enabled: !!user,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 1,
  });

  const loading = authLoading || (!!user && businessLoading);
  const isAdmin = businessData?.isAdmin || false;
  const ownsBusiness = businessData?.ownsBusiness || false;
  const isStaff = businessData?.isStaff || false;
  const staffOutletId = businessData?.staffOutletId || null;
  const staffBusinessId = businessData?.staffBusinessId || null;
  const isSupportCollaborator = businessData?.isSupportCollaborator || false;
  // For staff, businessId comes from their staff record; for admins, from their businesses row
  const businessId = businessData?.businessId || staffBusinessId || null;
  const onboardingComplete = user
    ? (businessLoading ? null : ((isStaff || isSupportCollaborator) ? true : (businessData?.onboardingComplete ?? false)))
    : false;
  const subscriptionTier = businessData?.subscriptionTier || null;
  const hasBookingsAddon = !!businessData?.hasBookingsAddon;

  useEffect(() => {
    let mounted = true;

    // A password-recovery session (e.g. the internal-onboarding "set your
    // password" link) must NOT be treated as a brand-new account trying to log
    // in. Otherwise the new-account guard below signs it out and bounces to
    // /auth before the client can set their password. Detect it from the URL
    // (the recovery link lands on /auth/reset-password with a type=recovery hash).
    const isRecoveryContext =
      typeof window !== "undefined" &&
      (window.location.pathname.startsWith("/auth/reset-password") ||
        window.location.hash.includes("type=recovery"));

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, currentSession) => {
        if (!mounted) return;
        console.log("[AuthContext] Auth event:", event);

        if (event === "PASSWORD_RECOVERY") {
          setIsPasswordRecovery(true);
        } else if (event === "SIGNED_OUT") {
          setIsPasswordRecovery(false);
        }

        if (currentSession && event === "SIGNED_IN") {
          const intent = localStorage.getItem("dibs_oauth_intent");
          if ((intent === "login" || !SELF_SERVE_ONBOARDING_ENABLED) && !isRecoveryContext && !isStaffProvisionedUser(currentSession.user)) {
            const timeDiff = Math.abs(new Date().getTime() - new Date(currentSession.user.created_at).getTime());
            if (timeDiff < 120000) {
              // IMPORTANT: never call supabase.auth.* synchronously inside the
              // onAuthStateChange callback. It runs while holding the auth lock,
              // so an awaited signOut here deadlocks concurrent auth calls (e.g.
              // updateUser on the password-reset page / another tab). Defer it.
              setTimeout(async () => {
                await supabase.auth.signOut();
                localStorage.removeItem("dibs_oauth_intent");
                window.location.href = "/auth?error=new_account_via_login";
              }, 0);
              return;
            }
          }
          if (intent) {
            localStorage.removeItem("dibs_oauth_intent");
          }
        }
        
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        if (currentSession?.user) {
          Sentry.setUser({ id: currentSession.user.id, email: currentSession.user.email });
        } else {
          Sentry.setUser(null);
        }
        setAuthLoading(false);
      }
    );

    // Initial session check
    supabase.auth.getSession().then(async ({ data: { session: initialSession } }) => {
      if (!mounted) return;
      
      if (initialSession) {
        const intent = localStorage.getItem("dibs_oauth_intent");
        if ((intent === "login" || !SELF_SERVE_ONBOARDING_ENABLED) && !isRecoveryContext && !isStaffProvisionedUser(initialSession.user)) {
          const timeDiff = Math.abs(new Date().getTime() - new Date(initialSession.user.created_at).getTime());
          if (timeDiff < 120000) {
            await supabase.auth.signOut();
            localStorage.removeItem("dibs_oauth_intent");
            window.location.href = "/auth?error=new_account_via_login";
            return;
          }
        }
        if (intent) {
          localStorage.removeItem("dibs_oauth_intent");
        }
      }

      setSession(initialSession);
      setUser(initialSession?.user ?? null);
      if (initialSession?.user) {
        Sentry.setUser({ id: initialSession.user.id, email: initialSession.user.email });
      } else {
        Sentry.setUser(null);
      }
      setAuthLoading(false);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const getRedirectUrl = () => {
    if (window.location.hostname.startsWith('admin.')) {
      return `${window.location.origin}/`;
    }
    return `${window.location.origin}/admin`;
  };

  const signInWithGoogle = async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: getRedirectUrl(),
        queryParams: {
          prompt: "select_account",
        },
      },
    });
    return { data, error };
  };

  const signInWithEmail = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { data, error };
  };

  const signUpWithEmail = async (email: string, password: string) => {
    const verifyUrl = `${window.location.origin}/auth/verify-success`;

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: verifyUrl,
      },
    });
    return { data, error };
  };

  const resetPasswordForEmail = async (email: string) => {
    const redirectUrl = `${window.location.origin}/auth/reset-password`;
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });
    return { data, error };
  };

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      localStorage.removeItem("dibs_oauth_intent");
      localStorage.removeItem("dibs_oauth_return_path");
      localStorage.removeItem("dibs_staff_active_business");
      localStorage.removeItem("dibs_collab_active_business");
      setUser(null);
      setSession(null);
    } catch (error) {
      console.error("Sign out error:", error);
    }
  };

  const switchStaffBusiness = (id: string) => {
    setSelectedStaffBusinessId(id);
    localStorage.setItem("dibs_staff_active_business", id);
    localStorage.removeItem("dibs_known_outlets");
    localStorage.removeItem("dibs_active_outlet");
    // Optional: force query refetch to update dependent queries, though changing state will trigger a re-render
  };

  const switchCollaboratorBusiness = (id: string) => {
    setSelectedCollabBusinessId(id);
    localStorage.setItem("dibs_collab_active_business", id);
    localStorage.removeItem("dibs_known_outlets");
    localStorage.removeItem("dibs_active_outlet");
  };

  const refreshBusinessContext = useCallback(async () => {
    await refetchBusinessData();
  }, [refetchBusinessData]);

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        isAdmin,
        ownsBusiness,
        isStaff,
        staffOutletId,
        staffBusinessId,
        staffBusinesses: businessData?.staffBusinesses || null,
        isSupportCollaborator,
        collaboratorBusinesses: businessData?.collaboratorBusinesses || null,
        collaboratorExpiresAt: businessData?.collaboratorExpiresAt || null,
        businessId,
        onboardingComplete,
        subscriptionTier,
        hasBookingsAddon,
        isPasswordRecovery,
        signInWithGoogle,
        signInWithEmail,
        signUpWithEmail,
        resetPasswordForEmail,
        refreshBusinessContext,
        signOut: handleSignOut,
        switchStaffBusiness,
        switchCollaboratorBusiness,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
